import { Component } from '@theme/component';

const LOG_PREFIX = '[form-injector]';

/**
 * Depth-first search for the first element (including inside open shadow
 * trees) for which `predicate` returns true. `root` itself is tested too.
 *
 * @param {Document | Element | ShadowRoot} root
 * @param {(element: Element) => boolean} predicate
 * @returns {Element | null}
 */
function findDeep(root, predicate) {
  if (root instanceof Element) {
    if (predicate(root)) return root;
    if (root.shadowRoot) {
      const found = findDeep(root.shadowRoot, predicate);
      if (found) return found;
    }
  }

  for (const child of root.children) {
    const found = findDeep(child, predicate);
    if (found) return found;
  }

  return null;
}

/**
 * A non-visual block that injects a key/value pair into a target form.
 *
 * The target form may not exist yet when this component connects (rendered
 * later by another script, app, or app embed), so it keeps watching the DOM
 * until the form appears rather than checking only once.
 *
 * @extends Component
 */
class FormInjector extends Component {
  /** @type {HTMLInputElement | null} */
  #createdInput = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** Dedupes noisy repeated log lines while the MutationObserver is polling. */
  #lastLoggedState = '';

  connectedCallback() {
    super.connectedCallback();
    console.debug(LOG_PREFIX, 'connected', { ...this.dataset });
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    console.debug(LOG_PREFIX, 'settings updated', { ...this.dataset });
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    console.debug(LOG_PREFIX, 'disconnected');
    this.#stopWatching();
    this.#createdInput?.remove();
    this.#createdInput = null;
  }

  #start() {
    this.#stopWatching();

    if (this.#inject()) return;

    console.debug(LOG_PREFIX, 'form not found yet, watching the DOM for changes');

    this.#observer = new MutationObserver(() => {
      if (this.#inject()) {
        console.debug(LOG_PREFIX, 'form appeared, injected and stopped watching');
        this.#stopWatching();
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  #stopWatching() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Logs `message`, skipping it if it's identical to the last message logged
   * (so the MutationObserver polling loop doesn't spam the console while it
   * keeps failing to find the form for the same reason).
   *
   * @param {string} message
   * @param {unknown} [details]
   */
  #log(message, details) {
    if (message === this.#lastLoggedState) return;
    this.#lastLoggedState = message;
    console.debug(LOG_PREFIX, message, details ?? '');
  }

  /**
   * @returns {boolean} Whether the target form was found (regardless of
   * whether a field already existed for the given key).
   */
  #inject() {
    const { formId, key, value } = this.dataset;

    this.#createdInput?.remove();
    this.#createdInput = null;

    if (!formId || !key) {
      this.#log('missing form_id or field_key setting, nothing to inject', { formId, key });
      return false;
    }

    const host = findDeep(document, (el) => el.id === `forms-root-${formId}` || el.getAttribute('data-forms-id') === `forms-root-${formId}`);

    if (!host) {
      this.#log(`no element found with id or data-forms-id "forms-root-${formId}"`);
      return false;
    }

    const form = findDeep(host, (el) => el instanceof HTMLFormElement);

    if (!(form instanceof HTMLFormElement)) {
      this.#log(`found element for "forms-root-${formId}", but it contains no <form> (yet)`, host);
      return false;
    }

    const existingField = form.elements.namedItem(key);
    const field = existingField instanceof RadioNodeList ? existingField[0] : existingField;

    if (field instanceof HTMLInputElement) {
      field.value = value ?? '';
      this.#log(`updated existing field "${key}" on form "forms-root-${formId}"`, { value, field, form });
      return true;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value ?? '';
    form.prepend(input);
    this.#createdInput = input;
    this.#log(`created hidden field "${key}" on form "${formId}"`, { value, form });
    return true;
  }
}

customElements.define('form-injector-component', FormInjector);
